// The engagement model's signal settings, beside the sample they shape.
//
// An engagement model has no signal definitions to write: every mapped event
// a lead performed is a signal, and what the settings decide is which
// aggregations join them and which activity types and events are left out as
// noise. Three pickers, where customer fit has an editor. The old page carried
// them on their own tab and sent people to a different section to see the
// effect; here the sample is in the next column.
//
// Same store, same actions as `LtbSignalsPage`, which stays mounted for a
// tenant without the revamp. The option shaping is copied from it rather than
// imported, because that file belongs to an open PR and the two pages will
// not both exist for long.
import { action } from 'mobx';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import isEqual from 'lodash/isEqual';

import { ModelType } from '../../../models/ModelItem';
import { isArchitectFor, PROFILES_PERMISSIONS } from '../../../permissions';
import Store from '../../../store';
import { IS_LLTB } from '../../../utils/constants';
import { stringifyAggregationName } from '../behavioral_aggregations/utils';
import checkPermissions from '../CheckPermissions';
import MadMultiSelect from '../generic_components/select_component/MadMultiSelect';
import {
  MultiSelectOption,
  Option,
} from '../generic_components/select_component/types';
import Spinner from '../Spinner';
import { buildAdminUrl } from '../../utils/adminUrl';
import './ltbSignalsForm.css';

const activityOption = (
  activityType: string,
  modelType: ModelType
): MultiSelectOption => ({
  value: activityType,
  label: activityType,
  // Always excluded: non-user activity is never a signal, and an MQL model
  // does not see product usage at all.
  isFixed:
    activityType === 'Non-user Activity' ||
    (modelType === 'mql' && activityType === 'App Usage'),
});

const aggregationOption = (name: string): Option => ({
  value: name,
  label: stringifyAggregationName(name),
});

const eventOption = (metaEvent: string): Option => ({
  value: metaEvent,
  label: metaEvent,
});

function LtbSignalsForm({
  store,
  onSave,
}: {
  store?: Store;
  onSave: () => Promise<void>;
}) {
  const [loading, setLoading] = useState(true);
  const {
    isAllowedToEdit,
    signalsPage,
    eventMetadata,
    tenant,
    userPermissions,
    isSuperKudu,
    behavioralAggregationsPage,
    activeModel,
    rbac,
  } = store;
  const { signals, savingSignals } = signalsPage;

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      store.loadEventMetadata(),
      behavioralAggregationsPage.get(tenant),
    ]).then(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  const canEdit = useMemo(
    () =>
      isAllowedToEdit &&
      !savingSignals &&
      (isArchitectFor({ permissions: userPermissions, rbac }, tenant) ||
        isSuperKudu),
    [isAllowedToEdit, savingSignals, userPermissions, rbac, tenant, isSuperKudu]
  );

  const aggregationOptions = useMemo(
    () =>
      behavioralAggregationsPage.aggregations
        .filter(
          ({ type }) =>
            (IS_LLTB(activeModel.type) && type === 'email') ||
            (activeModel.type === 'mqa' && type === 'account')
        )
        .filter(({ isProductEvent }) =>
          activeModel.type === 'mql' ? !isProductEvent : true
        )
        .map(({ name }) => aggregationOption(name)),
    [behavioralAggregationsPage.aggregations, activeModel.type]
  );
  const activityOptions = useMemo(
    () =>
      eventMetadata.activityTypes.map((type) =>
        activityOption(type, activeModel.type)
      ),
    [eventMetadata.activityTypes, activeModel.type]
  );
  const eventOptions = useMemo(
    () => eventMetadata.metaEventsDisplay?.map(eventOption) ?? [],
    [eventMetadata.metaEventsDisplay]
  );

  if (loading) return <Spinner />;
  // Loaded and still nothing: say so, where a spinner would spin for ever.
  if (!signals?.ltbSignals) {
    return (
      <p className="lsf-hint">This model has no signal settings to show.</p>
    );
  }
  const { ltbSignals } = signals;

  // Stored as one comma separated string; an empty one is no exclusion.
  const excludedTypes = ltbSignals.activityTypeExclusionList.split(', ');
  const activityValues = (
    isEqual(excludedTypes, ['']) ? [] : excludedTypes
  ).map((type) => activityOption(type, activeModel.type));

  return (
    <div className="lsf">
      <section className="lsf-section">
        <h6 className="lsf-label">Aggregations shown</h6>
        <p className="lsf-hint">
          Added to every record&apos;s signals, above its events.
        </p>
        <MadMultiSelect
          value={ltbSignals.aggregationsToInclude.map(aggregationOption)}
          options={aggregationOptions}
          onChange={action((values) => {
            ltbSignals.aggregationsToInclude = values.map(({ value }) => value);
          })}
          isDisabled={!canEdit}
          placeholder="Add an aggregation..."
        />
      </section>
      <section className="lsf-section">
        <h6 className="lsf-label">Activity types hidden</h6>
        <MadMultiSelect
          isFree
          value={activityValues}
          options={activityOptions}
          onChange={action((values) => {
            ltbSignals.activityTypeExclusionList = values
              .map(({ value }) => value)
              .join(', ');
          })}
          isDisabled={!canEdit}
          placeholder="Hide an activity type..."
        />
      </section>
      <section className="lsf-section">
        <h6 className="lsf-label">Events hidden</h6>
        <p className="lsf-hint">
          By the name the{' '}
          <a
            href={buildAdminUrl(tenant, '/mapping/event_mapping')}
            target="_blank"
            rel="noopener noreferrer"
          >
            event mapping
          </a>{' '}
          gives it under MK Event (signals).
        </p>
        <MadMultiSelect
          isFree
          options={eventOptions}
          value={ltbSignals.eventExclusionListFromSignals.map(eventOption)}
          onChange={action((values) => {
            ltbSignals.eventExclusionListFromSignals = values.map(
              ({ value }) => value
            );
          })}
          isDisabled={!canEdit}
          placeholder="Hide an event..."
        />
      </section>
      {checkPermissions(
        PROFILES_PERMISSIONS.ARCHITECT,
        <div className="lsf-actions">
          <Button
            size="sm"
            variant="primary"
            data-testid="signals-save"
            disabled={!canEdit}
            onClick={() => void onSave()}
          >
            {savingSignals ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default inject('store')(observer(LtbSignalsForm));
